import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
// On réutilise la librairie de DND du Kanban pour la fluidité
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Building2, FileText, CreditCard, Receipt, Save, Shield,
  Image as ImageIcon, AlertTriangle, Upload, Trash2,
  KanbanSquare, Plus, X, Lock, GripVertical, Mail
} from 'lucide-react';

const CompanySettingsPage = () => {
  const { token, isAdmin } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    company_name: '', legal_form: '', address: '', city: '', postal_code: '', country: 'France',
    email: '', phone: '', website: '', siret: '', rcs: '', vat_number: '',
    is_vat_subject: true, default_vat_rate: 20,
    bank_details: { iban: '', bic: '', bank_name: '' },
    logo_base64: '', primary_color: '#4f46e5',
    legal_mentions_default: '', quote_validity_days: 30, payment_terms: '',
    pipeline_stages: [],
    followup_email_subject: '', followup_email_body: ''
  });

  const userIsAdmin = isAdmin();
  
  const { data: settings, isLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/settings/company`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch settings');
      return res.json();
    },
  });

  useEffect(() => {
    if (settings) {
      // LOGIQUE DE RÉPARATION : Si Gagné/Perdu manquent, on les réinjecte par défaut
      let stages = settings.pipeline_stages || [];
      const hasWon = stages.find(s => s.id === 'won' || (s.is_system && s.name.toLowerCase().includes('gagn')));
      const hasLost = stages.find(s => s.id === 'lost' || (s.is_system && s.name.toLowerCase().includes('perdu')));

      if (!hasWon) stages.push({ id: 'won', name: 'Gagné', color: '#22c55e', is_system: true, order: 98 });
      if (!hasLost) stages.push({ id: 'lost', name: 'Perdu', color: '#ef4444', is_system: true, order: 99 });

      // Tri par ordre
      stages.sort((a, b) => a.order - b.order);

      setFormData({
        company_name: settings.company_name || '',
        legal_form: settings.legal_form || '',
        address: settings.address || '',
        city: settings.city || '',
        postal_code: settings.postal_code || '',
        country: settings.country || 'France',
        email: settings.email || '',
        phone: settings.phone || '',
        website: settings.website || '',
        siret: settings.siret || '',
        rcs: settings.rcs || '',
        vat_number: settings.vat_number || '',
        is_vat_subject: settings.is_vat_subject ?? true,
        default_vat_rate: settings.default_vat_rate || 20,
        bank_details: settings.bank_details || { iban: '', bic: '', bank_name: '' },
        logo_base64: settings.logo_base64 || '',
        primary_color: settings.primary_color || '#4f46e5',
        legal_mentions_default: settings.legal_mentions_default || '',
        quote_validity_days: settings.quote_validity_days || 30,
        payment_terms: settings.payment_terms || '',
        followup_email_body: settings.followup_body || '', // Attention au nom du champ backend vs frontend
        followup_email_subject: settings.followup_subject || '',
        pipeline_stages: stages
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      // Mapping inverse pour le backend (followup_email_body -> followup_body)
      const payload = {
          ...data,
          followup_body: data.followup_email_body,
          followup_subject: data.followup_email_subject
      };

      const res = await fetch(`${API_URL}/settings/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update settings');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      toast.success('Paramètres enregistrés');
    },
    onError: (err) => toast.error(err.message),
  });

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("L'image est trop volumineuse (max 500 Ko)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      updateField('logo_base64', reader.result);
      toast.success("Logo prêt");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateBankDetails = (field, value) => {
    setFormData(prev => ({ ...prev, bank_details: { ...prev.bank_details, [field]: value } }));
  };

  // --- LOGIQUE DRAG AND DROP ---
  const customStages = formData.pipeline_stages.filter(s => !s.is_system);
  const systemStages = formData.pipeline_stages.filter(s => s.is_system);

  const onDragEndWrapper = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(customStages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // On reconstruit la liste complète : [Custom triés] + [System fixes]
    const fullList = [...items, ...systemStages].map((item, index) => ({
        ...item,
        order: index
    }));
    
    updateField('pipeline_stages', fullList);
  };

  if (!userIsAdmin) return <div className="p-12 text-center text-muted-foreground">Accès restreint</div>;
  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6" data-testid="company-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-heading">Paramètres</h1>
          <p className="text-muted-foreground">Configuration générale</p>
        </div>
        <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
          <Save className="w-4 h-4 mr-2" /> Enregistrer
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="identity" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
            <TabsTrigger value="identity" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Building2 className="w-4 h-4" /> Identité</TabsTrigger>
            <TabsTrigger value="legal" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><FileText className="w-4 h-4" /> Légal</TabsTrigger>
            <TabsTrigger value="banking" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><CreditCard className="w-4 h-4" /> Banque</TabsTrigger>
            <TabsTrigger value="pipeline" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><KanbanSquare className="w-4 h-4" /> Pipeline</TabsTrigger>
            <TabsTrigger value="quotes" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Receipt className="w-4 h-4" /> Devis</TabsTrigger>
            <TabsTrigger value="automation" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Mail className="w-4 h-4" /> Automation</TabsTrigger>
          </TabsList>

          <TabsContent value="identity">
             <Card><CardContent className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Nom commercial *</Label><Input value={formData.company_name} onChange={e => updateField('company_name', e.target.value)} placeholder="Ma Super Entreprise" /></div>
                    <div className="space-y-2"><Label>Forme juridique</Label><Input value={formData.legal_form} onChange={e => updateField('legal_form', e.target.value)} placeholder="SARL, SAS..." /></div>
                </div>
                <div className="space-y-2"><Label>Adresse</Label><Input value={formData.address} onChange={e => updateField('address', e.target.value)} placeholder="123 Rue de l'Exemple" /></div>
                <div className="grid grid-cols-3 gap-4">
                    <Input placeholder="CP" value={formData.postal_code} onChange={e => updateField('postal_code', e.target.value)} />
                    <Input placeholder="Ville" value={formData.city} onChange={e => updateField('city', e.target.value)} />
                    <Input placeholder="Pays" value={formData.country} onChange={e => updateField('country', e.target.value)} />
                </div>
                <Separator />
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Email</Label><Input placeholder="contact@entreprise.com" value={formData.email} onChange={e => updateField('email', e.target.value)} /></div>
                    <div className="space-y-2"><Label>Téléphone</Label><Input placeholder="01 23 45 67 89" value={formData.phone} onChange={e => updateField('phone', e.target.value)} /></div>
                </div>
                <div className="space-y-2"><Label>Site web</Label><Input placeholder="https://..." value={formData.website} onChange={e => updateField('website', e.target.value)} /></div>
                <Separator />
                {/* Logo Section */}
                <div className="space-y-3">
                    <Label>Logo</Label>
                    <div className="flex gap-4 items-center">
                        {formData.logo_base64 ? (
                            <div className="relative group w-24 h-24 border rounded flex items-center justify-center p-1 bg-white">
                                <img src={formData.logo_base64} alt="Logo" className="max-w-full max-h-full object-contain" />
                                <button type="button" onClick={() => updateField('logo_base64', '')} className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Trash2/></button>
                            </div>
                        ) : (
                            <div className="w-24 h-24 border border-dashed rounded flex items-center justify-center text-muted-foreground"><ImageIcon className="opacity-50"/></div>
                        )}
                        <div className="space-y-2">
                             <Input id="logo-upload" type="file" onChange={handleLogoChange} accept="image/*" className="hidden" />
                             <Button type="button" variant="outline" onClick={() => document.getElementById('logo-upload').click()}><Upload className="w-4 h-4 mr-2"/> Choisir un fichier</Button>
                             <p className="text-xs text-muted-foreground">Max 500 Ko. PNG/JPG.</p>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 items-center mt-4">
                    <Label>Couleur principale</Label>
                    <Input type="color" value={formData.primary_color} onChange={e => updateField('primary_color', e.target.value)} className="w-12 h-8 p-1 cursor-pointer" />
                    <Input value={formData.primary_color} onChange={e => updateField('primary_color', e.target.value)} className="w-28" />
                </div>
             </CardContent></Card>
          </TabsContent>

          <TabsContent value="legal">
             <Card><CardContent className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>SIRET</Label><Input value={formData.siret} onChange={e => updateField('siret', e.target.value)} placeholder="123 456 789 00012" /></div>
                    <div className="space-y-2"><Label>RCS</Label><Input value={formData.rcs} onChange={e => updateField('rcs', e.target.value)} placeholder="RCS Paris B 123..." /></div>
                </div>
                <Separator />
                <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                    <div>
                        <Label>Assujetti à la TVA</Label>
                        <p className="text-xs text-muted-foreground">Décochez si vous êtes auto-entrepreneur non assujetti.</p>
                    </div>
                    <Switch checked={formData.is_vat_subject} onCheckedChange={v => updateField('is_vat_subject', v)} />
                </div>
                {formData.is_vat_subject ? (
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>N° TVA Intracommunautaire</Label><Input placeholder="FR12 345678901" value={formData.vat_number} onChange={e => updateField('vat_number', e.target.value)} /></div>
                        <div className="space-y-2"><Label>Taux par défaut (%)</Label><Input placeholder="20" type="number" step="0.1" value={formData.default_vat_rate} onChange={e => updateField('default_vat_rate', parseFloat(e.target.value))} /></div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded text-sm"><AlertTriangle className="w-4 h-4"/> Mention "TVA non applicable" ajoutée automatiquement.</div>
                )}
                <div className="space-y-2"><Label>Mentions légales par défaut (bas de page)</Label><Textarea value={formData.legal_mentions_default} onChange={e => updateField('legal_mentions_default', e.target.value)} rows={3} placeholder="Pénalités de retard..." /></div>
             </CardContent></Card>
          </TabsContent>

          <TabsContent value="banking">
             <Card><CardContent className="p-6 space-y-4">
                <div className="space-y-2"><Label>Nom de la banque</Label><Input placeholder="BNP Paribas" value={formData.bank_details.bank_name} onChange={e => updateBankDetails('bank_name', e.target.value)} /></div>
                <div className="space-y-2"><Label>IBAN</Label><Input placeholder="FR76 ..." value={formData.bank_details.iban} onChange={e => updateBankDetails('iban', e.target.value)} className="font-mono" /></div>
                <div className="space-y-2"><Label>BIC / SWIFT</Label><Input placeholder="BNPA..." value={formData.bank_details.bic} onChange={e => updateBankDetails('bic', e.target.value)} className="font-mono w-48" /></div>
             </CardContent></Card>
          </TabsContent>

          <TabsContent value="pipeline">
            <Card>
              <CardHeader><CardTitle>Configuration du Pipeline</CardTitle><CardDescription>Organisez vos étapes de vente.</CardDescription></CardHeader>
              <CardContent className="space-y-6">
                <DragDropContext onDragEnd={onDragEndWrapper}>
                  <Droppable droppableId="pipeline-list">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                        {customStages.map((stage, index) => (
                          <Draggable key={stage.id} draggableId={stage.id} index={index}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps} className={`flex items-center gap-3 p-3 border rounded-lg bg-card ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''}`}>
                                <div {...provided.dragHandleProps} className="cursor-grab text-muted-foreground"><GripVertical className="w-5 h-5" /></div>
                                <Input type="color" value={stage.color} onChange={(e) => {
                                    const newStages = [...formData.pipeline_stages];
                                    const realIndex = newStages.findIndex(s => s.id === stage.id);
                                    newStages[realIndex].color = e.target.value;
                                    updateField('pipeline_stages', newStages);
                                  }} className="w-8 h-8 p-0.5 rounded cursor-pointer border-none" />
                                <Input value={stage.name} onChange={(e) => {
                                    const newStages = [...formData.pipeline_stages];
                                    const realIndex = newStages.findIndex(s => s.id === stage.id);
                                    newStages[realIndex].name = e.target.value;
                                    updateField('pipeline_stages', newStages);
                                  }} className="flex-1 h-9" />
                                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive h-8 w-8" onClick={() => {
                                    const newStages = formData.pipeline_stages.filter(s => s.id !== stage.id);
                                    updateField('pipeline_stages', newStages);
                                  }}><X className="w-4 h-4" /></Button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <div className="space-y-2 pt-2 border-t mt-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Étapes de clôture (Fixes)</p>
                    {systemStages.map((stage) => (
                        <div key={stage.id} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30 opacity-80">
                            <div className="w-5 flex justify-center"><Lock className="w-3 h-3 text-muted-foreground" /></div>
                            <Input type="color" value={stage.color} onChange={(e) => {
                                    const newStages = [...formData.pipeline_stages];
                                    const realIndex = newStages.findIndex(s => s.id === stage.id);
                                    newStages[realIndex].color = e.target.value;
                                    updateField('pipeline_stages', newStages);
                            }} className="w-8 h-8 p-0.5 rounded cursor-pointer border-none" />
                            <div className="flex-1 font-medium text-sm px-3">{stage.name}</div>
                        </div>
                    ))}
                </div>

                <Button variant="outline" className="w-full border-dashed mt-4" disabled={formData.pipeline_stages.length >= 12} onClick={() => {
                    const newStages = [...customStages];
                    newStages.push({ id: `stage_${Date.now()}`, name: "Nouvelle étape", color: "#94a3b8", is_system: false, order: customStages.length });
                    const fullList = [...newStages, ...systemStages].map((s, i) => ({...s, order: i}));
                    updateField('pipeline_stages', fullList);
                  }}>
                  <Plus className="w-4 h-4 mr-2" /> Ajouter une étape
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quotes">
             <Card><CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                    <Label>Validité des devis (jours)</Label>
                    <div className="flex items-center gap-3">
                        <Input type="number" min="1" max="365" value={formData.quote_validity_days} onChange={e => updateField('quote_validity_days', parseInt(e.target.value) || 30)} className="w-24" />
                        <span className="text-sm text-muted-foreground">jours à partir de la date de création</span>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>Conditions de règlement par défaut</Label>
                    <Textarea value={formData.payment_terms} onChange={e => updateField('payment_terms', e.target.value)} placeholder="Acompte de 30%..." rows={2} />
                    <p className="text-xs text-muted-foreground">Peut être personnalisé pour chaque devis.</p>
                </div>
             </CardContent></Card>
          </TabsContent>

          <TabsContent value="automation">
            <Card>
              <CardHeader><CardTitle>Email de Relance Automatique</CardTitle><CardDescription>Modèle pour les prospects dormants.</CardDescription></CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md text-sm">
                    <p className="font-semibold mb-1">Variables :</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><code>{'{contact_name}'}</code> : Nom du prospect</li>
                        <li><code>{'{company_name}'}</code> : Votre entreprise</li>
                        <li><code>{'{lead_company}'}</code> : Entreprise du prospect</li>
                    </ul>
                </div>
                <div className="space-y-2"><Label>Objet</Label><Input value={formData.followup_email_subject || ''} onChange={e => updateField('followup_email_subject', e.target.value)} placeholder="Suite à notre échange..." /></div>
                <div className="space-y-2"><Label>Message</Label><Textarea value={formData.followup_email_body || ''} onChange={e => updateField('followup_email_body', e.target.value)} rows={10} placeholder="Bonjour {contact_name}..." className="font-mono text-sm" /></div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </form>

      {/* --- PREVIEW SECTION (RESTAURÉE) --- */}
      <Card className="border-dashed bg-muted/10 mt-8">
        <CardHeader>
          <CardTitle className="text-lg">Aperçu Visuel</CardTitle>
          <CardDescription>Voici comment vos informations apparaîtront en haut des devis et factures.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-white text-slate-900 p-8 rounded-lg border shadow-sm max-w-4xl mx-auto font-sans">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                {/* Logo ou Nom */}
                {formData.logo_base64 ? (
                    <img src={formData.logo_base64} alt="Logo Entreprise" className="h-16 object-contain mb-2" />
                ) : (
                    <h2 className="text-2xl font-bold tracking-tight text-primary" style={{ color: formData.primary_color }}>{formData.company_name || 'Nom de l\'entreprise'}</h2>
                )}
                
                {/* Infos Société */}
                <div className="text-sm text-slate-500 space-y-0.5 pt-1">
                    {formData.legal_form && <p>{formData.legal_form}</p>}
                    <p>{formData.address || 'Adresse complète'}</p>
                    <p>{formData.postal_code} {formData.city}</p>
                    <p>{formData.country}</p>
                    {formData.siret && <p className="text-xs pt-1">SIRET: {formData.siret}</p>}
                </div>
              </div>

              <div className="text-right space-y-2">
                <div className="text-3xl font-light text-slate-300 uppercase tracking-widest">DEVIS</div>
                <Badge variant="outline" className="text-xs font-normal" style={{ borderColor: formData.primary_color, color: formData.primary_color }}>
                  {formData.is_vat_subject ? 'Assujetti TVA' : 'TVA non applicable'}
                </Badge>
              </div>
            </div>

            {/* Pied de page simulation */}
            {formData.bank_details.iban && (
              <div className="mt-8 pt-4 border-t border-slate-100 flex gap-8 text-[10px] text-slate-400">
                <div>
                    <span className="font-semibold text-slate-600">Banque :</span> {formData.bank_details.bank_name}
                </div>
                <div>
                    <span className="font-semibold text-slate-600">IBAN :</span> {formData.bank_details.iban}
                </div>
                <div>
                    <span className="font-semibold text-slate-600">BIC :</span> {formData.bank_details.bic}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettingsPage;